import { useEffect, useState } from 'react';
import { Modal } from '../../../components/Modal';
import { Button } from '../../../components/Button';
import { Spinner } from '../../../components/Spinner';
import { listSites } from '../../../api/endpoints';
import type { Site } from '../../../types/api';

interface Props {
  open: boolean;
  customerCode: string;
  initialQuery?: string;
  onClose: () => void;
  onSelect: (code: string, name?: string, nameEditable?: boolean, abbr?: string) => void;
}

export function SiteSearchModal({ open, customerCode, onClose, onSelect }: Props) {
  const [query, setQuery] = useState('');
  const [allSites, setAllSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !customerCode) return;
    setQuery('');
    setLoading(true);
    void (async () => {
      try {
        const data = await listSites({ tokcd: customerCode });
        setAllSites(data.items);
      } catch { /* silent */ }
      setLoading(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerCode]);

  const filtered = query.trim()
    ? allSites.filter((s) => {
        const terms = query.trim().normalize('NFKC').toLowerCase().split(/\s+/);
        const name = (s.name || '').normalize('NFKC').toLowerCase();
        const abbr = (s.abbr || '').normalize('NFKC').toLowerCase();
        const code = s.code;
        return terms.every((t) => code.includes(t) || name.includes(t) || abbr.includes(t));
      })
    : allSites;

  return (
    <Modal open={open} onClose={onClose} title="現場検索" widthClass="max-w-3xl">
      <div className="flex gap-2 mb-3 items-center">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="現場名・コードで絞り込み"
          className="flex-1 border border-border rounded px-3 py-1.5 text-sm"
        />
        <span className="text-sm text-muted shrink-0">{filtered.length} / {allSites.length} 件</span>
      </div>

      {loading ? (
        <Spinner />
      ) : filtered.length === 0 ? (
        <p className="text-muted text-sm">
          {allSites.length === 0 ? 'この得意先に紐づく現場はありません' : '該当する現場がありません'}
        </p>
      ) : (
        <div className="overflow-auto max-h-[50vh]">
          <table>
            <thead>
              <tr>
                <th>コード</th>
                <th>名称</th>
                <th>略称</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.code}>
                  <td className="text-xs font-mono">{r.code}</td>
                  <td>{r.name}</td>
                  <td>{r.abbr}</td>
                  <td>
                    <Button onClick={() => onSelect(r.code, r.name || r.abbr, r.name_editable, r.abbr)}>選択</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
